import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  ShieldAlert,
  AlertTriangle,
  Shield,
  ShieldCheck,
  Crosshair,
  Swords,
  Target,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  History,
  Plus,
  Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { fetchRunsRemote, type RunSnapshot } from "@/lib/agentcore";
import { relativeTime, absoluteTime, isStale } from "@/lib/time";
import { computeRunDiff, type RunDiff } from "@/lib/diff";
import type { ThreatLevel } from "@/lib/swarmTypes";
import { Sparkline } from "./Dashboard";
import { ExportBar } from "./ExportBar";
import { AlertsToggle } from "./AlertsToggle";

/**
 * Competitor detail - the latest brief for one competitor plus its history,
 * threat trend, and a "What changed" diff vs the previous run. Read-only: all
 * data comes from fetchRunsRemote (the server writes snapshots on completion).
 */

interface CompetitorDetailProps {
  idToken: string;
  onAnalyze: (competitor: string) => void;
}

const LEVEL: Record<ThreatLevel, { label: string; Icon: LucideIcon; hex: string; text: string; chip: string; barFrom: string }> = {
  CRITICAL: { label: "Critical threat", Icon: ShieldAlert, hex: "#ef4444", text: "text-red-400", chip: "border-red-700/40 bg-red-500/10 text-red-300", barFrom: "from-red-500 to-red-400" },
  HIGH: { label: "High threat", Icon: AlertTriangle, hex: "#f59e0b", text: "text-amber-400", chip: "border-amber-600/40 bg-amber-500/10 text-amber-300", barFrom: "from-amber-500 to-amber-400" },
  MEDIUM: { label: "Medium threat", Icon: Shield, hex: "#22d3ee", text: "text-cyan-300", chip: "border-cyan-600/40 bg-cyan-500/10 text-cyan-300", barFrom: "from-cyan-500 to-cyan-400" },
  LOW: { label: "Low threat", Icon: ShieldCheck, hex: "#94a3b8", text: "text-slate-300", chip: "border-slate-600/40 bg-white/5 text-slate-300", barFrom: "from-slate-500 to-slate-400" },
};

export function CompetitorDetail({ idToken, onAnalyze }: CompetitorDetailProps) {
  const navigate = useNavigate();
  const params = useParams();
  const competitor = decodeURIComponent(params.name ?? "");

  const [runs, setRuns] = useState<RunSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [runsOk, setRunsOk] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await fetchRunsRemote(idToken, { competitor, limit: 50 });
      if (cancelled) return;
      if (!res.ok) {
        setRunsOk(false);
        setRuns([]);
        setLoading(false);
        return;
      }
      setRunsOk(true);
      const sorted = [...(res.runs ?? [])].sort((a, b) => Date.parse(b.runAt) - Date.parse(a.runAt));
      setRuns(sorted);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [idToken, competitor]);

  const latest = runs[0] ?? null;
  const diff = useMemo(() => computeRunDiff(runs), [runs]);
  const scores = useMemo(() => [...runs].reverse().map((r) => r.score), [runs]);
  const theme = latest ? LEVEL[latest.level] : LEVEL.MEDIUM;

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Back + heading */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/app")}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-200 transition-colors cursor-pointer mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Watchlist
          </button>
          <h1 className="font-mono text-2xl sm:text-3xl font-bold tracking-tight text-white truncate">{competitor}</h1>
          {latest && (
            <p className="mt-1 text-[11px] text-slate-600" title={absoluteTime(latest.runAt)}>
              Analyzed {relativeTime(latest.runAt)}
              {isStale(latest.runAt) && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-600/30 bg-amber-500/5 px-2 py-0.5 text-[10px] font-medium text-amber-400">
                  <Clock className="w-3 h-3" /> Stale
                </span>
              )}
            </p>
          )}
        </div>
        <button
          onClick={() => onAnalyze(competitor)}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 hover:shadow-glow-brand cursor-pointer min-h-[44px]"
        >
          <Play className="w-4 h-4 fill-current" />
          Run again
        </button>
      </div>

      {loading ? (
        <DetailSkeleton />
      ) : !latest ? (
        <EmptyDetail competitor={competitor} runsOk={runsOk} onAnalyze={() => onAnalyze(competitor)} />
      ) : (
        <>
          {/* What changed */}
          {diff && <WhatChanged diff={diff} />}

          {/* Latest threat snapshot */}
          <section data-print="region" className="ns-glass rounded-2xl p-5 sm:p-6 shadow-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg border ${theme.chip}`}>
                  <theme.Icon className={`w-4 h-4 ${theme.text}`} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">Latest threat assessment</p>
                  <h2 className="font-mono text-base font-bold text-white truncate leading-tight">{competitor}</h2>
                </div>
              </div>
              <ExportBar
                compact
                payload={{
                  competitor,
                  runAt: latest.runAt,
                  threat: {
                    competitor,
                    score: latest.score,
                    level: latest.level,
                    rationale: latest.rationale,
                    whyItMattersToUs: latest.whyItMattersToUs ?? "",
                    confidence: latest.confidence ?? 0,
                    dimensions: latest.dimensions,
                    topThreats: latest.topThreats,
                    counterMoves: latest.counterMoves,
                    sources: latest.sources ?? [],
                  },
                }}
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex items-center gap-4">
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`font-mono text-4xl font-bold leading-none ${theme.text}`}>{latest.score}</span>
                    <span className="text-xs font-mono text-slate-600">/ 10</span>
                  </div>
                  <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${theme.chip}`}>
                    <theme.Icon className="w-3.5 h-3.5" />
                    {theme.label}
                  </span>
                </div>
                <Sparkline scores={scores} hex={theme.hex} width={120} height={48} />
              </div>
              {latest.rationale && (
                <p className="min-w-0 flex-1 text-sm text-slate-300 leading-relaxed">{latest.rationale}</p>
              )}
            </div>

            {latest.whyItMattersToUs && (
              <div className="mt-5 rounded-xl border border-[#22d3ee]/25 bg-[#22d3ee]/[0.06] p-4">
                <div className="flex items-center gap-2">
                  <Crosshair className="w-4 h-4 text-[#22d3ee] shrink-0" />
                  <p className="text-[11px] font-semibold text-[#22d3ee] uppercase tracking-[0.18em]">Why it matters to us</p>
                </div>
                <p className="mt-2 text-sm text-slate-200 leading-relaxed">{latest.whyItMattersToUs}</p>
              </div>
            )}

            {latest.dimensions.length > 0 && (
              <div className="mt-5 flex flex-col gap-3">
                {latest.dimensions.map((d) => {
                  const pct = d.max > 0 ? Math.min(100, Math.max(0, (d.score / d.max) * 100)) : 0;
                  return (
                    <div key={d.key}>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-slate-300 truncate">{d.label}</span>
                        <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">{d.score}<span className="text-slate-600">/{d.max}</span></span>
                      </div>
                      <div className="h-2 rounded-full bg-[#0a1322] border border-[#1a2640]/60 overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${theme.barFrom}`} style={{ width: `${pct}%`, transition: "width 800ms cubic-bezier(0.16,1,0.3,1)" }} />
                      </div>
                      {d.note && <p className="mt-1 text-[11px] text-slate-500 leading-snug">{d.note}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {(latest.topThreats.length > 0 || latest.counterMoves.length > 0) && (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {latest.topThreats.length > 0 && (
                  <SignalList title="Top threats" items={latest.topThreats} Icon={Swords} iconClass="text-amber-400" dotClass="bg-amber-400/70" />
                )}
                {latest.counterMoves.length > 0 && (
                  <SignalList title="Counter-moves" items={latest.counterMoves} Icon={Target} iconClass="text-emerald-400" dotClass="bg-emerald-400/70" />
                )}
              </div>
            )}
          </section>

          {/* SEC 8-K filing alerts - subscribe this competitor to email + Slack
              alerts on new high-signal filings. Self-fetches its initial state. */}
          <AlertsToggle competitor={competitor} />

          {/* History timeline */}
          {runs.length > 1 && (
            <section className="ns-glass rounded-2xl p-5 sm:p-6 shadow-card">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-3.5 h-3.5 text-slate-500" />
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em]">Run history</p>
              </div>
              <ul className="flex flex-col gap-2.5">
                {runs.map((r) => {
                  const t = LEVEL[r.level];
                  return (
                    <li key={r.runId} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-lg border ${t.chip} shrink-0`}>
                          <span className={`font-mono text-xs font-bold ${t.text}`}>{r.score}</span>
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-200">{t.label}</p>
                          <p className="text-[11px] text-slate-600" title={absoluteTime(r.runAt)}>{relativeTime(r.runAt)}</p>
                        </div>
                      </div>
                      <span className="text-[11px] text-slate-600 shrink-0">{r.sourceCount} source{r.sourceCount === 1 ? "" : "s"}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ── What changed (diff badge/section) ────────────────────────────────────────

function WhatChanged({ diff }: { diff: RunDiff }) {
  const up = diff.direction === "up";
  const down = diff.direction === "down";
  const DeltaIcon = up ? TrendingUp : down ? TrendingDown : Minus;
  // A rising threat is "bad" (red/amber up arrow); a falling threat is "good" (green).
  const deltaColor = up ? "text-red-400" : down ? "text-emerald-400" : "text-slate-400";
  const deltaChip = up
    ? "border-red-700/40 bg-red-500/10"
    : down
      ? "border-emerald-700/40 bg-emerald-500/10"
      : "border-[#1a2640] bg-white/[0.03]";

  return (
    <section className="rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">What changed</span>
          <span className="text-[11px] text-slate-600">since {relativeTime(diff.previousRunAt)}</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${deltaChip} ${deltaColor}`}>
          <DeltaIcon className="w-3.5 h-3.5" />
          {diff.scoreDelta === 0 ? "No score change" : `${diff.scoreDelta > 0 ? "+" : ""}${diff.scoreDelta} threat score`}
        </span>
      </div>

      {diff.levelChanged && (
        <p className="mt-3 text-sm text-slate-300">
          Threat level moved from <span className="font-semibold text-slate-100">{diff.previousLevel}</span> to{" "}
          <span className="font-semibold text-slate-100">{diff.currentLevel}</span>.
        </p>
      )}

      {(diff.newThreats.length > 0 || diff.resolvedThreats.length > 0) && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {diff.newThreats.length > 0 && (
            <div className="rounded-xl border border-red-700/25 bg-red-500/[0.04] p-3.5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Plus className="w-3.5 h-3.5 text-red-400" />
                <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">New threats</p>
              </div>
              <ul className="flex flex-col gap-2">
                {diff.newThreats.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
                    <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-red-400/70" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {diff.resolvedThreats.length > 0 && (
            <div className="rounded-xl border border-emerald-700/25 bg-emerald-500/[0.04] p-3.5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">Resolved / dropped</p>
              </div>
              <ul className="flex flex-col gap-2">
                {diff.resolvedThreats.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                    <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-emerald-400/70" />
                    <span className="line-through decoration-slate-700">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!diff.levelChanged && diff.newThreats.length === 0 && diff.resolvedThreats.length === 0 && diff.scoreDelta === 0 && (
        <p className="mt-3 text-sm text-slate-500">No material change since the previous run.</p>
      )}
    </section>
  );
}

function SignalList({ title, items, Icon, iconClass, dotClass }: { title: string; items: string[]; Icon: LucideIcon; iconClass: string; dotClass: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
        <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">{title}</p>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
            <span className={`mt-1.5 w-1 h-1 rounded-full shrink-0 ${dotClass}`} />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="ns-glass rounded-2xl p-6 space-y-4" role="status" aria-label="Loading competitor history">
      <div className="h-8 w-24 rounded bg-white/5 animate-pulse" />
      <div className="h-12 w-full rounded bg-white/5 animate-pulse" />
      <div className="space-y-2">
        {[92, 78, 85].map((w, i) => (
          <div key={i} className="h-2.5 rounded bg-white/5 animate-pulse" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  );
}

function EmptyDetail({ competitor, runsOk, onAnalyze }: { competitor: string; runsOk: boolean; onAnalyze: () => void }) {
  return (
    <div className="ns-glass rounded-2xl p-10 text-center flex flex-col items-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
        <Play className="w-5 h-5 fill-current" />
      </div>
      <h2 className="mt-4 font-mono text-lg font-bold text-white">No briefs for {competitor} yet</h2>
      <p className="mt-2 max-w-md text-sm text-slate-500 leading-relaxed">
        {runsOk
          ? "Run your first analysis to start tracking the threat trend and see what changes between runs."
          : "Run history will appear here once your first brief completes."}
      </p>
      <button
        onClick={onAnalyze}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
      >
        <Play className="w-4 h-4 fill-current" />
        Run analysis
      </button>
    </div>
  );
}
